import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface ClientDealsSchedulingFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    meta: any;
}

const state: ClientDealsSchedulingFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,

    meta: {},
};

const initState: ClientDealsSchedulingFormState = cloneDeep(state);

export const PRICE_ITEM_DATA = 'GET_PRICE_ITEM_DATA';
export const PRICE_ITEM_SCHEMA = 'GET_PRICE_ITEM_SCHEMA';

export const actions: ActionTree<ClientDealsSchedulingFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRICE_ITEM_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', result.getOperation(PRICE_ITEM_DATA).getContent());

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(PRICE_ITEM_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:client_deals_schedule`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(PRICE_ITEM_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        let normalizedForms: any[] = []
        if (Array.isArray(state.form.weekday)) {
            normalizedForms = state.form.weekday.map((item: any) => {
                return prepareFormObjectForSave(state.schema, {
                    ...state.form,
                    weekday: item
                });

            }) 
        } else {
            normalizedForms = [
                prepareFormObjectForSave(state.schema, state.form)
            ]
        }

        console.log(normalizedForms)

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = [
                    vm.$q('PRICE_ITEM_DATA_SAVE-0')
                        .update(documentId, normalizedForms[0])
                ]
            } else {
                saveQuery = normalizedForms.map((item: any, idx: number) => {
                    return vm.$q('PRICE_ITEM_DATA_SAVE-' + idx)
                        .create('s:client_deals_schedule', item)
                })
            }

            vm.$db(saveQuery).then((result: ServerResult) => {
                const operationResult = result.getOperation('PRICE_ITEM_DATA_SAVE-0');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    setArchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: moment().toISOString()
            });

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

    setUnarchive({state, commit}) {
        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const vm = (this as any)._vm;

            const normalizedForm = prepareFormObjectForSave(state.schema, {
                archived_at: null
            });

            const saveQuery = vm.$q('SAVE')
                .update(state.form.id, normalizedForm)
            vm.$db([
                saveQuery
            ]).then(((result: ServerResult) => {
                commit('LOADED_SUCCESS');
                resolve();
            }));
        })
    },

};

export const mutations: MutationTree<ClientDealsSchedulingFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_FORM_DATA(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        
        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ClientDealsSchedulingFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ClientDealsSchedulingFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_deal_sheduling_details_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
